import React from 'react';
import NumberFlow from '@number-flow/react';
import type { SpringValue } from '@react-spring/web';
import { Block, Inline } from '@contentful/rich-text-types';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';

import {
  formatCurrency,
  makeImageSource,
  makeImageQueryString,
} from 'src/lib/utils';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import {
  Card,
  Canvas,
  CanvasWrapper,
  Disclaimer,
  Draggable,
  DraggableWrapper,
  GradientIcon,
  GradientDefs,
  ToggleInput,
  ToggleLabel,
} from './ACHCalculator.styled';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { Typography } from 'src/components-v2/DataDisplay/Typography';
import type { IUserInput, ICalculation } from './ACHCalculator.types';
import type { Sys } from 'src/types/contentful-sdk-compat';
import type { ComponentAsset } from 'src/lib/ts-types';

/** Unique canvas id for gradient so multiple calculators on the same page don't conflict (Mortgage also uses a gradient canvas). */
export const GRADIENT_ACH_CANVAS_ID = 'gradient-ach-calculator';

interface ACHCardProps {
  handleDragStart: (e: React.DragEvent) => void;
  handleDragOver: () => void;
  handleDragEnd: () => void;
  translateDraggable: SpringValue<string>;
  userInput: IUserInput;
  togglePeriod: ({ target: { value } }: { target: { value: string } }) => void;
  handleToggle: (e: React.KeyboardEvent) => void;
  translateWrapper: SpringValue<string>;
  translateCanvas: SpringValue<string>;
  isPotato: boolean;
  calcState: ICalculation;
  sys?: Sys;
  fields?: {
    cardBackgroundAsset?: ComponentAsset;
    cardDisclaimer?: Block | Inline;
    trackingContext?: Record<string, unknown>;
    styles?: SxProps<ITheme>;
  };
}

export const ACHCard: React.FC<ACHCardProps> = ({
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  translateDraggable,
  userInput,
  togglePeriod,
  handleToggle,
  translateWrapper,
  translateCanvas,
  isPotato,
  calcState,
  sys,
  fields,
}) => {
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(
    fields?.cardBackgroundAsset?.fields,
  );

  return (
    <Card>
      <Stack
        sx={{
          gap: 3.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          zIndex: 1,
        }}
      >
        <DraggableWrapper
          direction='row'
          gap={1}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          sx={{
            margin: { xs: 0, sm: '0 auto' },
            transform: {
              xs: 'translateX(-50%) scale(1)',
              sm: 'translateX(0%) scale(1)',
            },
            left: { xs: '50%', sm: 0 },
          }}
        >
          <Draggable
            draggable={true}
            style={{ transform: translateDraggable }}
          />
          <ToggleLabel
            htmlFor='ach-calculator-yearly'
            isChecked={userInput?.months === 12}
          >
            <ToggleInput
              type='radio'
              id='ach-calculator-yearly'
              name='period'
              value='12'
              checked={userInput?.months === 12}
              onChange={togglePeriod}
              onKeyDown={handleToggle}
            />
            Yearly
          </ToggleLabel>
          <ToggleLabel
            htmlFor='ach-calculator-monthly'
            sx={{
              left: 'calc(50% - 3px)', // width of first toggle label
            }}
            isChecked={userInput?.months === 1}
          >
            <ToggleInput
              type='radio'
              id='ach-calculator-monthly'
              name='period'
              value='1'
              checked={userInput?.months === 1}
              onChange={togglePeriod}
              onKeyDown={handleToggle}
            />
            Monthly
          </ToggleLabel>
          <CanvasWrapper
            style={{ transform: translateWrapper }}
            draggable={true}
          >
            <Canvas
              id={GRADIENT_ACH_CANVAS_ID}
              style={{ transform: translateCanvas }}
            />
          </CanvasWrapper>
        </DraggableWrapper>
        <Stack
          sx={{
            width: '100%',
          }}
        >
          <Typography
            variant='h4'
            component='span'
            sx={{ fontSize: { xs: '2.4rem', sm: '2.8rem' } }}
          >
            Potential savings
          </Typography>
          <Typography
            variant='h2'
            component='span'
            sx={{ fontSize: { xs: '4.0rem', sm: '6.0rem' }, mb: '-15px' }}
          >
            <NumberFlow
              animated={!isPotato}
              value={calcState.savings}
              format={{
                style: 'currency',
                currency: 'USD',
                trailingZeroDisplay: 'stripIfInteger',
              }}
            />
          </Typography>
        </Stack>
        <hr />
        <Stack gap={{ xs: 2, sm: 3.5 }}>
          <Typography
            variant='h4'
            component='span'
            sx={{
              mb: { xs: 0, sm: '14px' },
              fontSize: '2.8rem',
            }}
          >
            Compare fees
          </Typography>
          <Stack
            direction='row'
            gap={1}
            justifyContent='space-between'
            sx={{
              'p, span': {
                fontSize: { xs: '1.4rem', sm: '1.8rem' },
              },
            }}
          >
            <Typography variant='p5' component='span'>
              Before pay by bank
            </Typography>
            <Typography variant='p5' component='span'>
              {formatCurrency({
                value: calcState.beforePBB,
                showDecimals: false,
              })}
            </Typography>
          </Stack>
          <Stack
            direction='row'
            gap={1}
            justifyContent='space-between'
            sx={{
              'p, span': {
                fontSize: { xs: '1.4rem', sm: '1.8rem' },
              },
            }}
          >
            <Typography
              variant='p5'
              component='p'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <b>After pay by bank</b>
              <GradientIcon icon='LightningFilled' size={20} />
            </Typography>
            <Typography variant='p5' component='p'>
              <b>
                {formatCurrency({
                  value: calcState.afterPBB,
                  showDecimals: false,
                })}
              </b>
            </Typography>
          </Stack>
        </Stack>
        <hr />
        <Disclaimer>
          <RichTextSwitch
            sys={sys}
            content={fields?.cardDisclaimer}
            nodes={{
              p: {
                variant: 'p',
                component: 'p',
                fieldId: 'cardDisclaimer',
              },
            }}
          />
        </Disclaimer>
      </Stack>
      <CmsMediaSwitch
        role='presentation'
        asset={fields?.cardBackgroundAsset?.fields}
        sys={fields?.cardBackgroundAsset?.sys}
        queryString={{
          xl: `?fm=webp&w=1122&q=60`,
        }}
        sources={{
          xs: makeImageQueryString({ src: smImgSrc, width: 561 }),
          sm: makeImageQueryString({ src: smImgSrc, width: 527 }),
          md: makeImageQueryString({ src: mdImgSrc, width: 1304 }),
          lg: makeImageQueryString({ src: lgImgSrc, width: 816 }),
          xl: makeImageQueryString({ src: lgImgSrc, width: 1122 }),
        }}
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          left: 0,
        }}
      />
      <GradientDefs />
    </Card>
  );
};
